import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { GLOBAL_ROLE_LABELS, type GlobalRole } from '@/auth/permissions'
import {
  Alert,
  Avatar,
  Badge,
  Card,
  EmptyState,
  Skeleton,
  StatusDot,
  Table,
  Td,
  Th,
} from '@/components/ui'
import './pages.css'

interface UserRow {
  id: string
  email: string
  full_name: string | null
  avatar_url: string | null
  department: string | null
  job_title: string | null
  status: 'active' | 'suspended'
  user_global_roles: { role_key: GlobalRole }[] | null
}

/**
 * Mitarbeiterverzeichnis.
 *
 * Rollenänderungen laufen ausschliesslich ueber assign_global_role() — die
 * Tabelle user_global_roles ist fuer 'authenticated' schreibgeschuetzt. Die
 * Oberflaeche dafuer kommt mit dem Verwaltungsmodul; die Anzeige steht schon,
 * weil sie beim Einrichten gebraucht wird.
 */
export function AdminUsers() {
  const [users, setUsers] = useState<UserRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    supabase
      .from('profiles')
      .select(
        'id, email, full_name, avatar_url, department, job_title, status, user_global_roles(role_key)',
      )
      .order('full_name', { nullsFirst: false })
      .then(({ data, error: queryError }) => {
        if (!active) return
        if (queryError) setError(queryError.message)
        else setUsers((data ?? []) as unknown as UserRow[])
      })

    return () => {
      active = false
    }
  }, [])

  return (
    <div className="stack-lg">
      <header className="page-header">
        <p className="eyebrow">Verwaltung</p>
        <h1>Personen</h1>
        <p className="text-secondary prose">
          Alle Konten des Intranets. Gesperrte Konten behalten ihr Profil, verlieren
          aber jeden Datenzugriff — auch dann, wenn ihr Token noch gültig ist.
        </p>
      </header>

      {error && (
        <Alert tone="danger" title="Abfrage fehlgeschlagen">
          {error}
        </Alert>
      )}

      {users === null && !error && <Skeleton height="200px" />}

      {users?.length === 0 && (
        <EmptyState
          title="Keine Konten sichtbar"
          description="Für die Anzeige wird das Recht user:read benötigt."
        />
      )}

      {users && users.length > 0 && (
        <Card flush>
          <Table>
            <thead>
              <tr>
                <Th>Person</Th>
                <Th>Abteilung</Th>
                <Th>Globale Rolle</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const role = user.user_global_roles?.[0]?.role_key
                return (
                  <tr key={user.id}>
                    <Td>
                      <span className="cluster-sm">
                        <Avatar
                          name={user.full_name ?? user.email}
                          src={user.avatar_url}
                          size="sm"
                        />
                        <span className="stack" style={{ gap: 0 }}>
                          <span>{user.full_name ?? '—'}</span>
                          <span className="text-tertiary" style={{ fontSize: 'var(--text-2xs)' }}>
                            {user.email}
                          </span>
                        </span>
                      </span>
                    </Td>
                    <Td>{user.department ?? '—'}</Td>
                    <Td>
                      {role ? (
                        <Badge tone={role === 'guest' ? 'neutral' : 'accent'}>
                          {GLOBAL_ROLE_LABELS[role]}
                        </Badge>
                      ) : (
                        <span className="text-tertiary">nicht freigeschaltet</span>
                      )}
                    </Td>
                    <Td>
                      <StatusDot
                        tone={user.status === 'active' ? 'success' : 'danger'}
                        label={user.status === 'active' ? 'Aktiv' : 'Gesperrt'}
                      />
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  )
}

interface AuditRow {
  id: number
  actor_id: string | null
  action: string
  resource_type: string | null
  resource_id: string | null
  at: string
}

/** Änderungsprotokoll — append-only, auch fuer Owner nicht editierbar. */
export function AdminAudit() {
  const [entries, setEntries] = useState<AuditRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    supabase
      .from('audit_log')
      .select('id, actor_id, action, resource_type, resource_id, at')
      .order('at', { ascending: false })
      .limit(100)
      .then(({ data, error: queryError }) => {
        if (!active) return
        if (queryError) setError(queryError.message)
        else setEntries((data ?? []) as AuditRow[])
      })

    return () => {
      active = false
    }
  }, [])

  return (
    <div className="stack-lg">
      <header className="page-header">
        <p className="eyebrow">Verwaltung</p>
        <h1>Änderungsprotokoll</h1>
        <p className="text-secondary prose">
          Jede Rechte- und Kontoänderung wird in derselben Transaktion protokolliert
          wie die Änderung selbst. Einträge lassen sich nicht nachträglich ändern
          oder löschen — auch nicht mit Owner-Rechten.
        </p>
      </header>

      {error && (
        <Alert tone="danger" title="Abfrage fehlgeschlagen">
          {error}
        </Alert>
      )}

      {entries === null && !error && <Skeleton height="200px" />}

      {entries?.length === 0 && (
        <EmptyState
          title="Noch keine Einträge"
          description="Sobald Rollen vergeben, Bereiche angelegt oder Einladungen verschickt werden, erscheinen sie hier."
        />
      )}

      {entries && entries.length > 0 && (
        <Card flush>
          <Table>
            <thead>
              <tr>
                <Th>Zeitpunkt</Th>
                <Th>Aktion</Th>
                <Th>Objekt</Th>
                <Th>Auslöser</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <Td numeric>
                    {new Date(entry.at).toLocaleString('de-DE', {
                      dateStyle: 'short',
                      timeStyle: 'medium',
                    })}
                  </Td>
                  <Td>
                    <code className="perm-chip">{entry.action}</code>
                  </Td>
                  <Td>
                    {entry.resource_type ? (
                      <span className="text-tertiary numeric">
                        {entry.resource_type}/{entry.resource_id?.slice(0, 8) ?? '—'}
                      </span>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td>
                    <span className="text-tertiary numeric">
                      {entry.actor_id?.slice(0, 8) ?? 'System'}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  )
}
